/**
 * Types shared between the simulation, the worker protocol and the UI.
 */

import type { Distribution } from '../core/math/random'

export type AgentState =
  'walking' | 'queuing' | 'waiting' | 'served' | 'seated' | 'dwelling' | 'done'

export const AGENT_STATE_ORDER: AgentState[] = [
  'walking',
  'queuing',
  'waiting',
  'served',
  'seated',
  'dwelling',
  'done',
]

export const agentStateIndex = (state: AgentState): number => AGENT_STATE_ORDER.indexOf(state)

/** Packed per-agent record in a snapshot. Keep in step with `AGENT_STRIDE`. */
export const AGENT_STRIDE = 10
export const AGENT_FIELD = {
  x: 0,
  y: 1,
  heading: 2,
  speed: 3,
  state: 4,
  profile: 5,
  population: 6,
  /** Seconds this person has spent waiting so far. */
  waited: 7,
  radius: 8,
  id: 9,
} as const

export interface SimStats {
  time: number
  spawned: number
  active: number
  completed: number
  /** Persons per square metre, averaged over occupied floor. */
  meanDensity: number
  peakDensity: number
  meanSpeed: number
  /** Mean speed of people currently trying to get somewhere. */
  meanWalkingSpeed: number
  /** How many people are currently trying to get somewhere. */
  walking: number
  /** Share of those people moving below 0.3 m/s — the congestion indicator. */
  stoppedShare: number
  meanWait: number
  maxWait: number
  queueLengths: Array<{
    id: string
    name: string
    waiting: number
    served: number
    meanWait: number
  }>
  /** Fruin level of service of the busiest measured area. */
  worstLos: string
}

export interface AgentJourney {
  id: number
  populationId: string
  profileId: string
  spawnedAt: number
  finishedAt: number | null
  /** Total path length in metres. */
  distance: number
  /** Time spent below 0.3 m/s, in seconds. */
  stoppedTime: number
  /** Time spent queueing, in seconds. */
  queueTime: number
  /** Time from spawn to leaving, in seconds. */
  totalTime: number | null
  /** Straight-line distance travelled divided by path length; 1 is a direct walk. */
  directness: number
}

export interface ServiceSummary {
  id: string
  name: string
  servers: number
  served: number
  /**
   * Over everybody whose service began, finished or not; null when nobody's
   * did. So a run can end with a mean wait and nobody served yet.
   */
  meanWait: number | null
  maxWait: number
  /** Over the same people as `meanWait`, as their service was drawn. */
  meanService: number | null
  /** Share of run time the counters were busy. */
  utilisation: number
  maxQueue: number
  /** People still waiting when the run ended. */
  unserved: number
}

/** What happened inside one measurement area over the whole run. */
export interface AreaSummary {
  id: string
  name: string
  areaSqm: number
  /** Most people inside at any one moment. */
  peakOccupancy: number
  /** Time-averaged number of people inside. */
  meanOccupancy: number
  peakDensity: number
  meanDensity: number
  /** Mean speed of people while they were inside, in m/s. */
  meanSpeed: number
  /** Total person-seconds spent inside. */
  personSeconds: number
  /** Seconds spent at or worse than level of service E. */
  secondsAtLosE: number
  /** Seconds spent at level of service F. */
  secondsAtLosF: number
  /** Seconds the area was at or above 4 persons/m2. */
  secondsAtCrushRisk: number
  /** Worst level of service reached. */
  worstLos: string
}

export interface RunSummary {
  durationS: number
  seed: number
  totalPeople: number
  completed: number
  /**
   * In the venue when the run ended. The rest of those who did not complete
   * had not come in yet.
   */
  stillInside: number
  /** Mean time from arrival to leaving. Null when nobody has left. */
  meanJourney: number | null
  p95Journey: number | null
  /** Over everybody whose service began at any counter; null when nobody's did. */
  meanWait: number | null
  maxWait: number
  meanQueueTime: number | null
  /** Time by which 95% of people had left, in seconds. Null until they have. */
  clearanceTime: number | null
  walkableArea: number
  peakOccupancy: number
  peakDensity: number
  /** Share of simulated person-seconds spent in each level of service. */
  losShare: Record<string, number>
  services: ServiceSummary[]
  areas: AreaSummary[]
  warnings: string[]
}

export interface SimOptions {
  /** Physics timestep, in seconds. */
  timeStep: number
  /**
   * Navigation grid resolution, in metres. Omitted, the world picks one from
   * the geometry — see `chooseCellSize`, which sizes it by the narrowest
   * doorway people have to get through.
   */
  cellSize?: number
  /** Cap on simulated people, to keep a browser tab responsive. */
  maxAgents: number
}

export const DEFAULT_SIM_OPTIONS: SimOptions = {
  timeStep: 0.1,
  maxAgents: 6000,
}

export interface DwellSpec {
  duration: Distribution
}
